import {
    Table,
    Card,
    Checkbox,
    Form,
    Button,
} from 'antd';
import {useForm} from 'antd/es/form/Form';
import React, {useEffect, useState} from 'react';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {DynamicIcon} from 'components/dynamic-icon';
import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';
import {Spinner} from 'components/spinner';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {
    updateDashboardSettingsAction,
} from 'store/api/settings-dashboards/settings-dashboards.actions';
import {
    useFetchAllDashboardsSelectsPostMutation,
    useLazyFetchAllDashboardsQuery,
    useLazyFetchAllDashboardsSelectsQuery,
    useFetchAllDashboardsSettingsPostMutation,
} from 'store/api/settings-dashboards/settings-dashboards.api';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {
    ICheckboxItem,
    IDashboardFilterEntity,
    IDashColumnItem,
    IOptionItem,
    IDictionary,
    TDashboardDictionaryType,
} from './dashboards-types';

import './dashboards.less';

export const Dashboards = () => {
    const [updateDashboards, dashboardsRequestResult] = useLazyFetchAllDashboardsQuery();
    const [updateDashboardsSelects, dashboardsSelectsRequestResult] = useLazyFetchAllDashboardsSelectsQuery();
    const [uploadDashboard] = useFetchAllDashboardsSettingsPostMutation();
    const [uploadDashboardList] = useFetchAllDashboardsSelectsPostMutation();
    const [initialData, setInitialData] = useState<IDictionary<TDashboardDictionaryType>>();
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [values, setValues] = useState<IDictionary<TDashboardDictionaryType>>();
    const [form] = useForm();
    const [emptySelects, setEmptySelects] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const {data: dashboards} = dashboardsRequestResult || {};
    const {data: dashboardsSelects} = dashboardsSelectsRequestResult || {};

    const validateSelects = () => {
        const formValues = form.getFieldsValue();
        if (!dashboards || !dashboardsSelects || !formValues) return;

        const dashboardsWithSelects = dashboardsSelects.filter(selects => selects.selects.length);
        const currentActiveDashboards: IDashboardFilterEntity[] = [];

        dashboardsWithSelects.forEach(dashboard => {
            if (formValues[dashboard.itemKey]) currentActiveDashboards.push(dashboard);
        });

        const selects = currentActiveDashboards.map(
            dashboardsSelect => dashboardsSelect.selects,
        ).flat().filter(
            select => (Array.isArray(formValues[select.selectCode])
                ? !formValues[select.selectCode].length : !formValues[select.selectCode]),
        );

        setEmptySelects(selects?.map(select => select.selectCode));
    };

    const handleSave = (submitData:IDictionary<TDashboardDictionaryType>) => {
        form.validateFields();
        if (form.isFieldsTouched() && !emptySelects.length) {
            setValues(submitData);
            setConfirmVisible(true);
        }
    };
    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: ['app.app.settings.user.settings.dashboards'],
    });

    const save = () => {
        if (values && dashboards) {
            const dashboardSettings = dashboards.map(dashboard => ({
                isFavorite: dashboard.isFavorite,
                isCustomize: dashboard.isCustomize,
                itemKey: dashboard.menuKey,
                menuName: dashboard.entityName,
                name: dashboard.name,
            }));
            updateDashboardSettingsAction({
                values,
                dashboards: dashboardSettings,
                dashboardsSelects,
                setInitialData,
                uploadDashboard,
                uploadDashboardList,
            });
        }
        setConfirmVisible(false);
    };

    const handleCancel = () => {
        form.resetFields();
    };

    const handleCloseConfirmModal = () => {
        setConfirmVisible(false);
        form.resetFields();
    };

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await updateDashboardsSelects(null);
            await updateDashboards(null);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        const initialCheckBox: IDictionary<TDashboardDictionaryType> = {};
        dashboards?.forEach(item => { initialCheckBox[item.menuKey] = item.isFavorite; });
        const initialSelects: IDictionary<TDashboardDictionaryType> = {};
        dashboardsSelects?.forEach(item => {
            item?.selects?.forEach(select => {
                const optItems: number[] = [];
                select.selectValues.forEach(selVal => {
                    if (selVal.isActive) {
                        optItems.push(selVal.value.id);
                    }
                });
                initialSelects[select.selectCode] = optItems;
            });
        });
        setInitialData({...initialCheckBox, ...initialSelects});
    }, [dashboards, dashboardsSelects]);

    useEffect(() => {
        form.resetFields();
    }, [initialData]);

    const dashSource: IDashColumnItem[] | undefined = dashboards?.map((item, i) => {
        const selectsItems = dashboardsSelects?.find(itemSel => itemSel.itemKey === item.menuKey);
        const {description, isFavorite} = item;
        return ({
            key: i,
            dashboardName: item.name,
            description,
            dashboardSelects: selectsItems,
            isFavorite: {
                name: item.menuKey,
                value: isFavorite,
            },
        } as IDashColumnItem);
    });

    const columns = [
        {
            title: 'Дашборд',
            dataIndex: 'dashboardName',
            key: 'dashboardName',
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Параметр по умолчанию',
            dataIndex: 'dashboardSelects',
            key: 'dashboardSelects',
            render: (opts: IDashboardFilterEntity | undefined) => {
                if (!opts || isLoading) {
                    return (
                        <Spinner />
                    );
                }
                return (
                    <div>
                        {
                            opts?.selects?.map(selects => {
                                const selectList: IOptionItem[] | undefined = selects.selectValues.map(selVal => ({
                                    value: selVal.value.id,
                                    label: selVal.value.meaning,
                                } as IOptionItem));
                                return (
                                    <div>
                                        <span>{selects.selectTitle}:</span>
                                        <Form.Item
                                            name={selects.selectCode}
                                            rules={[{
                                                required: emptySelects.includes(selects.selectCode),
                                                message: 'Поле обязательно к заполнению',
                                            }]}
                                        >
                                            <CustomSelect
                                                entries={selectList}
                                                settings={{
                                                    placeholder: `Выберите ${selects.selectTitle.toLowerCase()}`,
                                                    mode: ECustomSelectMode.multiple,
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                );
                            })
                        }
                    </div>
                );
            },
        },
        {
            title: 'Избранный',
            dataIndex: 'isFavorite',
            key: 'isFavorite',
            render: (item: ICheckboxItem) => (
                <Form.Item
                    name={item.name}
                    valuePropName="checked"
                >
                    <Checkbox defaultChecked={item.value} />
                </Form.Item>
            ),
        },
    ];

    return (
        <Card
            className="dashboards"
            title="Дашборды"
        >
            <Form
                form={form}
                onFinish={submitData => {
                    handleSave(submitData);
                }}
                initialValues={initialData}
                onValuesChange={() => {
                    validateSelects();
                }}
            >
                <div
                    className="dashboards__buttons"
                >
                    {hasUserRoleFunction(EUserRoleFunction.CREATE) && (
                        <Button
                            type="primary"
                            htmlType="submit"
                        >
                            <DynamicIcon type="SaveOutlined" />
                            Сохранить
                        </Button>
                    )}
                    {hasUserRoleFunction(EUserRoleFunction.CANCEL) && (
                        <Button
                            type="default"
                            onClick={handleCancel}
                        >
                            <DynamicIcon type="CloseXOutlined" />
                            Отменить
                        </Button>
                    )}
                </div>
                {!isLoading ? (
                    <Table
                        dataSource={dashSource}
                        columns={columns}
                    />
                )
                    : <Spinner />}
            </Form>
            <ConfirmModal
                title="Подтверждение"
                visible={confirmVisible || false}
                content="Требуется подтвердить изменения"
                onCancel={handleCloseConfirmModal}
                cancelText="Нет"
                onConfirm={save}
                approveText="Да"
                useWrapperDivElementAsContainer
            />
        </Card>
    );
};
